import React, { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import { useDispatch } from "react-redux";
import axiosConfig from "../../Service/AxiosConfig";
import { updateTempForm } from "../../redux/BookingFormSlice";
import { FaChevronDown } from "react-icons/fa";
import Select from "react-select";
import { PopupModal } from "../../PopupModal/PopupModal";

const BasicInfo = ({ form, setForm, fetchBooking }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [editModePatient, setEditModePatient] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [popupType, setPopupType] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [language, setLanguage] = useState([]);
  const firstFieldRef = useRef(null);
  const hasFocusedOnce = useRef(false);
  const genderOptions = [
    { value: "Male", label: "Male" },
    { value: "Female", label: "Female" },
    { value: "Other", label: "Other" },
  ];
  const experienceOptions = Array.from({ length: 120 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));
  const fieldsToCheck = [
    "name",
    "gender",
    "date_of_birth",
    "years_of_experience",
    "languages",
  ];
  const hasAnyData = fieldsToCheck.some((key) => {
    const value = form?.[key];

    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return value === true;
    return value && String(value).trim() !== "";
  });

  useEffect(() => {
    if (!form?.id) return;
    if (!isDirty) {
      setEditModePatient(!hasAnyData);
    }
  }, [form?.id]);

  useEffect(() => {
    if (!expanded) return;
    const isApiDataEmpty = fieldsToCheck.every(
      (key) => !form?.[key] || String(form[key]).trim() === "",
    );
    if ((isApiDataEmpty || !hasFocusedOnce.current) && firstFieldRef.current) {
      setTimeout(() => {
        firstFieldRef.current.focus();
        hasFocusedOnce.current = true;
      }, 0);
    }
  }, [expanded, form]);

  useEffect(() => {
    axiosConfig
      .get("/masters/languages/?is_suspended=false")
      .then((r) => setLanguage(r.data.results || []));
  }, []);
  const languageOptions = language.map((l) => ({
    value: l.id,
    label: l.language,
  }));
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (!editModePatient) {
      setEditModePatient(true);
    }
    setIsDirty(true);
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    dispatch(
      updateTempForm({
        booking_id: form.id,
        data: { [name]: value },
      }),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("name", form.name || "");
      payload.append("gender", form.gender || "");
      if (form?.date_of_birth) {
        payload.append("date_of_birth", form.date_of_birth || "");
      }
      payload.append("years_of_experience", form.years_of_experience || "");
      (form.languages || []).forEach((langId) => {
        payload.append("languages", langId);
      });
      await axiosConfig.patch(`/provider-data/providers/${form.id}/`, payload);
      setEditModePatient(false);
      setIsDirty(false);
      setExpanded(false);
      setPopupType("success");
      fetchBooking();
      setMessage("Basic Info Saved Successfully");
    } catch (err) {
      setPopupType("error");
      console.log(err, "error");
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Failed to save details",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Accordion
        key={expanded ? "open" : "closed"}
        allowZeroExpanded
        preExpanded={expanded ? ["Basic"] : []}
        onChange={(uuids) => {
          setExpanded(uuids.includes("Basic"));
        }}
      >
        <AccordionItem uuid="Basic">
          <AccordionItemHeading>
            <AccordionItemButton className="bd-header">
              <h4 className="bd-title">Basic Information</h4>
              <span className="bd-add-btn">
                Add Details
                <FaChevronDown
                  style={{
                    transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                    marginLeft: "5px",
                  }}
                />
              </span>
            </AccordionItemButton>
          </AccordionItemHeading>

          <AccordionItemPanel className="bd-panel">
            <div className="bd-section-grid bd-grid-3 service-cards">
              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Full Name
                </label>
                <input
                  ref={firstFieldRef}
                  name="name"
                  value={form?.name || ""}
                  placeholder="Full Name"
                  onChange={handleChange}
                  disabled={!editModePatient}
                  className={`form-control ${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>
              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Gender
                </label>
                <Select
                  options={genderOptions}
                  value={genderOptions.find(
                    (opt) => opt.value === form?.gender,
                  )}
                  onChange={(selected) => {
                    if (!selected) return;

                    handleChange({
                      target: {
                        name: "gender",
                        value: selected.value,
                      },
                    });
                  }}
                  isDisabled={!editModePatient}
                  placeholder="Select gender"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={`${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>
              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Date of Birth
                </label>
                <input
                  name="date_of_birth"
                  type="date"
                  value={form?.date_of_birth || ""}
                  placeholder="Date of Birth"
                  onChange={handleChange}
                  disabled={!editModePatient}
                  className={`form-control ${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>
              <div className="form-group">
                <label
                  className={`form-label ${!editModePatient ? "view-mode-label" : ""}`}
                >
                  Years of Experience
                </label>

                <Select
                  options={experienceOptions}
                  value={experienceOptions.find(
                    (opt) => opt.value === form?.years_of_experience,
                  )}
                  onChange={(selected) => {
                    if (!selected) return;

                    handleChange({
                      target: {
                        name: "years_of_experience",
                        value: selected.value,
                      },
                    });
                  }}
                  isDisabled={!editModePatient}
                  placeholder="Select Experience"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={`${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>
              <div className="form-group">
                <label
                  className={`form-label ${
                    !editModePatient ? "view-mode-label" : ""
                  }`}
                >
                  Language
                </label>
                <Select
                  isMulti
                  options={languageOptions}
                  value={languageOptions.filter((opt) =>
                    (form.languages || []).includes(opt.value),
                  )}
                  onChange={(selected) => {
                    const values = selected ? selected.map((s) => s.value) : [];
                    setForm((prev) => ({
                      ...prev,
                      languages: values,
                    }));
                    setIsDirty(true);
                  }}
                  isDisabled={!editModePatient}
                  placeholder="Select Languages"
                  isSearchable={false}
                  classNamePrefix="height-select"
                  className={`${!editModePatient ? "view-mode-text" : ""}`}
                />
              </div>

              <div className="bd-actions">
                {!editModePatient ? (
                  <button
                    className="bd-edit-btn"
                    onClick={() => {
                      setEditModePatient(true);
                      setIsDirty(true);
                      setExpanded(true);
                    }}
                  >
                    Edit
                  </button>
                ) : (
                  hasAnyData &&
                  editModePatient && (
                    <button
                      className="bd-save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      Save
                    </button>
                  )
                )}
              </div>
            </div>
          </AccordionItemPanel>
        </AccordionItem>
      </Accordion>
      {popupType && (
        <PopupModal
          setpopupType={setPopupType}
          popupType={popupType}
          error={error}
          Message={message}
          setMessage={setMessage}
        />
      )}
    </>
  );
};

export default BasicInfo;
